import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Stack, Button, Dialog, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http from '../Api/http';
import { GetBusinessEntitiesDashboard } from '../Api/Api';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const GRADIENT = 'linear-gradient(135deg, #135F93 0%, #2F8EBD 100%)';

const UNLOCKS = [
    { icon: GroupsRoundedIcon, label: 'Employees & departments' },
    { icon: PaidRoundedIcon, label: 'Payroll & payslips' },
    { icon: EventAvailableRoundedIcon, label: 'Attendance & leave' },
];

export default function NoEntityGate() {
    const navigate = useNavigate();
    const location = useLocation();
    const isMasterAdmin = useSelector(selectUserTypeId) === USER_TYPE.MASTER_ADMIN;

    const [checked, setChecked] = useState(false);
    const [hasEntities, setHasEntities] = useState(true);
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!isMasterAdmin) { setChecked(true); return; }
        setLoading(true);
        try {
            const { data: body } = await http.get(GetBusinessEntitiesDashboard);
            const list = Array.isArray(body?.data?.entities) ? body.data.entities : [];
            setHasEntities(list.length > 0);
        } catch {
            setHasEntities(true);
        } finally {
            setLoading(false);
            setChecked(true);
        }
    }, [isMasterAdmin]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (isMasterAdmin && checked && !hasEntities) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    if (!isMasterAdmin || !checked || hasEntities) return null;

    const goCreate = () => {
        setOpen(false);
        navigate('/dashboard/entities', { state: { openCreate: true } });
    };

    return (
        <>
            {!open && (
                <Box sx={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1400 }}>
                    <Button
                        onClick={() => setOpen(true)}
                        startIcon={<ApartmentRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{ background: GRADIENT, color: '#fff', fontWeight: 800, fontSize: 13, textTransform: 'none', borderRadius: '999px', height: 46, px: 2.4, boxShadow: '0 12px 26px -10px rgba(19,95,147,0.8)', '&:hover': { filter: 'brightness(0.96)' } }}
                    >
                        Create your first entity
                    </Button>
                </Box>
            )}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: '16px', overflow: 'hidden' } } }}
            >
                <Box sx={{ background: GRADIENT, p: 3, color: '#fff', textAlign: 'center' }}>
                    <Box sx={{ width: 54, height: 54, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.18)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <RocketLaunchRoundedIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Welcome to ARA HumanSync</Typography>
                    <Typography sx={{ fontSize: 12.5, opacity: 0.9, mt: 0.4 }}>Let&apos;s get your workspace ready</Typography>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.65, textAlign: 'center' }}>
                        Start by creating your first <strong>business entity</strong> — your company. Everything in the app lives under an entity, so this comes first.
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 2.4 }}>
                        {UNLOCKS.map((u) => (
                            <Stack key={u.label} direction="row" spacing={1.4} sx={{ alignItems: 'center', p: 1.2, borderRadius: '11px', border: '1px solid #EEF1F6', bgcolor: '#F3F8FC' }}>
                                <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <u.icon sx={{ fontSize: 18, color: PRIMARY }} />
                                </Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{u.label}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #F1F3F7', gap: 1 }}>
                    <Button
                        onClick={() => setOpen(false)}
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#64748B', borderRadius: '8px', height: 42, px: 2 }}
                    >
                        Maybe later
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={goCreate}
                        disabled={loading}
                        endIcon={loading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{ background: GRADIENT, color: '#fff', textTransform: 'none', fontWeight: 800, fontSize: 13, borderRadius: '8px', height: 42, px: 2.6, boxShadow: 'none', '&:hover': { filter: 'brightness(0.96)' } }}
                    >
                        Create your first entity
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
